import type { PropsWithChildren } from 'react'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { NavWrapper } from 'components/OfferPage/styles'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import useBedbankRates, { useBedbankRatesForIndividualRooms } from 'hooks/useBedbankRates'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import type { AggregatedMultiRoomPrices } from 'lib/hotels/bedbankUtils'
import { getDisplayTotalPrice, getDisplayTotalPricePerNight } from 'lib/hotels/bedbankUtils'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { getBedbankMultiRoomPricingTotals } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import useGhaVerifierPrice from 'hooks/useGhaVerifierPrice'

const NavLayoutContainer = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: ${rem(8)};
`

const StyledPackageOptionsButton = styled(TextButton)`
  margin-inline-start: auto;
`

const PriceSummarySection = styled(Group)`
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: auto;
    padding-inline-start: ${rem(12)};
    margin-inline-start: auto;
  }
`

interface Props {
  duration: number
  onViewPackageOptions?: () => void
  onCheckAvailabilityClick?: () => void
  offer: App.BedbankOffer
  rooms: Array<App.Occupants>
  checkInDate?: string
  checkOutDate?: string
  flightPrice?: number
  flightsEnabled: boolean
  luxPlusLPPEnabled: boolean
  airport?: App.Airport
  multiRoomPrices?: AggregatedMultiRoomPrices
  isMultiRoomIndividualMode: boolean
}

function BedbankOfferHeader(props: PropsWithChildren<Props>) {
  const {
    children,
    onViewPackageOptions,
    onCheckAvailabilityClick,
    offer,
    rooms,
    checkInDate,
    checkOutDate,
    duration,
    flightsEnabled,
    luxPlusLPPEnabled,
    airport,
    multiRoomPrices,
    isMultiRoomIndividualMode,
  } = props

  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  useHeaderStateObserver('bedbankOfferHeader', ref)
  const [{
    hotelOnlyRates,
    hotelBundleRates,
  }, fetching] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)

  const [, individualRatesFetching] = useBedbankRatesForIndividualRooms(
    isMultiRoomIndividualMode ? offer.id : undefined,
    rooms,
    checkInDate,
    checkOutDate,
    isMultiRoomIndividualMode,
  )

  const sell = useBedbankOfferSell(offer, duration, rooms)
  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: airport?.code,
  })

  const useMultiRoomPrices = !!multiRoomPrices
  const isFetching = isMultiRoomIndividualMode ? individualRatesFetching : fetching

  const hotelPrice = useMultiRoomPrices ? multiRoomPrices.hotelPrice :
    hotelOnlyRates[0] ? hotelOnlyRates[0].totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = useMultiRoomPrices ? multiRoomPrices.hotelMemberPrice :
    hotelOnlyRates[0] ? hotelOnlyRates[0].totals.memberInclusive ?? 0 : 0
  const bundlePrice = useMultiRoomPrices ? multiRoomPrices.bundlePrice :
    hotelBundleRates[0]?.totals.inclusive || hotelPrice
  const hotelNights = multiRoomPrices?.hotelNights ?? hotelOnlyRates[0]?.nights ?? sell?.los
  const propertyFees = useMultiRoomPrices ? multiRoomPrices.propertyFees :
    hotelOnlyRates[0]?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundlePropertyFees = useMultiRoomPrices ? multiRoomPrices.bundlePropertyFees :
    hotelBundleRates[0]?.totals.propertyFees || propertyFees

  const datesSelected = !!(checkInDate && checkOutDate)
  const hasRates = useMultiRoomPrices || hotelOnlyRates.length > 0
  const showSummary = (datesSelected && hasRates) || (!datesSelected && sell)
  const isSoldOut = !useMultiRoomPrices && !isMultiRoomIndividualMode && hotelOnlyRates.length === 0 && datesSelected
  const baseSaleUnit = 'room'
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : baseSaleUnit

  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle
  const showOneNightPrice = !offer.displayPricingAsPerPerson && !checkInDate && !!hotelNights && hotelNights > 1
  const nightsToDisplay = showOneNightPrice ? 1 : hotelNights

  const totalBasePrices = useMemo(() => {
    return getDisplayTotalPrice({
      hotelPrice: hotelPrice + propertyFees,
      hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
      bundlePrice: bundlePrice + bundlePropertyFees,
      flightPrice,
      checkIn: checkInDate,
      rooms,
      displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    })
  }, [bundlePrice, bundlePropertyFees, checkInDate, flightPrice, hotelMemberPrice, hotelPrice, offer.displayPricingAsPerPerson, propertyFees, rooms])

  const totalPrices = showOneNightPrice ? getDisplayTotalPricePerNight(totalBasePrices, hotelNights) : totalBasePrices
  const offerPrice = promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price
  const offerMemberPrice = promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice
  const [discountAmount, discountFetching] = useMetasearchDiscount(offerPrice, {
    propertyId: offer.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    rooms,
    disabled: promoteAsBundle,
  })
  const displayOfferPrice = useGhaVerifierPrice(Math.max(offerPrice - discountAmount, 0), promoteAsBundle)
  const displayOfferMemberPrice = offerMemberPrice ? Math.max(offerMemberPrice - discountAmount, 0) : offerMemberPrice
  const isPriceFetching = isFetching || discountFetching

  const showModal = useContext(ModalContext)
  const onLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const checkAvailabilityText = intl.formatMessage({
    id: 'cW-wgIX7eUYRLUl5vidy',
    defaultMessage: 'Check availability',
    description: 'Check availability button text for bedbank offer header on mobile/tablet',
  })

  const mobileTabletButtonText = datesSelected ? intl.formatMessage({
    id: '6PeABGfdF-t9THAtfDYg',
    defaultMessage: 'View options',
    description: 'View options button text for bedbank offer header on mobile/tablet',
  }) : checkAvailabilityText

  const desktopButtonText = datesSelected ? intl.formatMessage(
    {
      id: '4ZuUTosXMlx8v_n8Hm1H',
      defaultMessage: 'View package options',
      description: 'View travel options button text for bedbank offer header on desktop',
    },
  ) : checkAvailabilityText

  const priceCaptionText = promoteAsBundle
    ? intl.formatMessage(
        {
          id: '7j0eU2hMY9eXWsvQZTgf',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} + flights from',
          description: 'Price caption showing nights and flights text for bedbank offer header',
        },
        { nights: nightsToDisplay },
      )
    : intl.formatMessage(
        {
          id: 'JWeoRMEEV-YFIR9WzChJ',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
          description: 'Price caption showing nights text for bedbank offer header',
        },
        { nights: nightsToDisplay },
      )

  return (
    <NavWrapper>
      <NavLayoutContainer>
        <div>
          {children}
        </div>
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={(
            <PriceSummarySection
              direction="horizontal"
              verticalAlign="center"
              horizontalAlign="space-between"
              tabletHorizontalAlign="end"
              gap={16}
            >
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'lpIYR8twgn-KKWdnynR-',
                  defaultMessage: 'Sign up to access exclusive rates',
                  description: 'Sign up message for bedbank offer header when pricing is walled',
                })}
              </BodyText>
              <Group direction="horizontal" gap={8} shrink={false}>
                <TextButton kind="secondary" onClick={onLogin}>
                  {intl.formatMessage({
                    id: 'pBiUzhNBZH_GNPN8IWzz',
                    defaultMessage: 'Log In',
                    description: 'Log in button text for bedbank offer header on the offer page',
                  })}
                </TextButton>
                <TextButton kind="primary" onClick={onLogin}>
                  <CSSBreakpoint max="mobile" as="span">
                    {intl.formatMessage({
                      id: 'dAtBtm0vtYFAxymdC6KN',
                      defaultMessage: 'Sign up',
                      description: 'Sign up button text for bedbank offer header on mobile',
                    })}
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet" as="span">
                    {intl.formatMessage({
                      id: 'TXFlXW18NTBh-xUb7VvP',
                      defaultMessage: 'Sign up for free',
                      description: 'Sign up button text for bedbank offer header on tablet and above',
                    })}
                  </CSSBreakpoint>
                </TextButton>
              </Group>
            </PriceSummarySection>
          )}
        >
          <PriceSummarySection
            direction="horizontal"
            verticalAlign="center"
            horizontalAlign="space-between"
            tabletHorizontalAlign="end"
            gap={12}
          >
            {showSummary && !isSoldOut && !isPriceFetching && <Group direction="vertical" tabletHorizontalAlign="end">
              <Group direction="vertical" horizontalAlign="center" noDisplayWhenEmpty>
                <LuxPlusOfferVoucherCallout offer={offer} mode="offer" />
              </Group>
              <PriceRowPriceCaption>
                {priceCaptionText}
              </PriceRowPriceCaption>
              <LuxPlusPriceGuard
                showMemberPrice={(displayOfferMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  data-testid="bedbank-sticky-header-price"
                  size="S"
                  price={displayOfferPrice}
                  saleUnit={saleUnit}
                />}
              >
                <PriceRowLuxPlusPrice
                  size="S"
                  memberPrice={displayOfferMemberPrice ?? 0}
                  saleUnit={saleUnit}
                  logoPlacement="leading"
                />
              </LuxPlusPriceGuard>
            </Group>}
            {isSoldOut && !isPriceFetching && <>
              <BodyText
                variant="large"
                weight="bold"
              >
                {intl.formatMessage({
                  id: 'kUcMSTfb1CJl78mBF6-k',
                  defaultMessage: 'Sold out on your dates',
                  description: 'Sold out message for bedbank offer header when no availability',
                })}
              </BodyText>
              <TextButton kind="primary" to="/hotels">
                {intl.formatMessage(commonMessages.browseAllHotelOffers)}
              </TextButton>
            </>}
            {!isSoldOut && <StyledPackageOptionsButton kind="primary" onClick={datesSelected ? onViewPackageOptions : onCheckAvailabilityClick}>
              <CSSBreakpoint max="tablet">
                {mobileTabletButtonText}
              </CSSBreakpoint>
              <CSSBreakpoint min="desktop">
                {desktopButtonText}
              </CSSBreakpoint>
            </StyledPackageOptionsButton>}
          </PriceSummarySection>
        </WalledContent>
      </NavLayoutContainer>
    </NavWrapper>
  )
}

export default withOfferPageAndReduxState((offerState, state) => {
  const isMultiRoomIndividualMode = getCanUseMultiRoomUI(offerState, state) && offerState.multiRoomMode === 'individual'
  return {
    offer: offerState.bedbankOffer!,
    rooms: offerState.rooms,
    duration: offerState.duration,
    checkInDate: offerState.checkInDate,
    checkOutDate: offerState.checkOutDate,
    airport: offerState.airport,
    open: !!offerState.activeFilter,
    luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
    isMultiRoomIndividualMode,
    multiRoomPrices: isMultiRoomIndividualMode ? getBedbankMultiRoomPricingTotals(offerState, state) : undefined,
  }
}, BedbankOfferHeader, preventRenderWithOpenFilter)
